import { useMemo, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  useTable,
  useFilters,
  usePagination,
  useAsyncDebounce,
  useGlobalFilter,
} from 'react-table';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import {
  Box,
  Table,
  Tooltip,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  IconButton,
  TableContainer,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';

import { DefaultColumnFilter } from '../ColumnFilter';

const FunctionalTable = ({
  data,
  type,
  onEdit,
  columns,
  fetchData,
  applyChanges,
  cancelChanges,
  isActionColumn = true,
  isFilter = true,
  hasMoreData,
  isInfiniteScroll = false,
  pageSize: customPageSize = 25,
  globalFilter: customGlobalFilter,
  editRow = true,
  actionColumnComponent,
  getRowSx,
}) => {
  const [searchParams] = useSearchParams();
  const searchParamsObj = Object.fromEntries(searchParams);
  const searchFilters = Object.keys(searchParamsObj).map(key => ({
    id: key,
    value: searchParamsObj[key],
  }));

  const [isShowFilter, setIsShowFilter] = useState(false);
  const [fetchId, setFetchId] = useState(0);

  const { t } = useTranslation(['common']);
  const scrollableWrapperRef = useRef(null);
  const infiniteScrollRef = useRef(null);

  const resetPage = () => {
    setFetchId(0);
    setTimeout(() => {
      infiniteScrollRef?.current?.el?.scrollTo(0, 0);
    }, 0);
  };

  const defaultColumn = useMemo(
    () => ({
      Filter: cell => (
        <DefaultColumnFilter
          cell={cell}
          debounce={useAsyncDebounce}
          resetPage={resetPage}
        />
      ),
    }),
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setHiddenColumns,
    setGlobalFilter,
    state: { pageSize, filters, globalFilter },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
        hiddenColumns: columns
          .filter(col => col.show === false)
          .map(col => col.id),
        filters: searchFilters,
        globalFilter: customGlobalFilter,
      },
      resetPage,
      isShowFilter,
      defaultColumn,
      manualFilters: true,
      manualGlobalFilter: true,

      manualPagination: true,
    },
    useFilters,
    useGlobalFilter,
    usePagination,

    hooks => {
      hooks.visibleColumns.push(columns =>
        isActionColumn
          ? [
              {
                id: 'actions',
                disableFilters: true,
                className: 'center action',
                style: {
                  width: '1%',
                  minWidth: actionColumnComponent && editRow ? '80px' : '70px',
                  maxWidth: actionColumnComponent && editRow ? '80px' : '70px',
                },
                Header: ({ isShowFilter }) => {
                  return isFilter ? (
                    <Tooltip
                      title={t(
                        `promptbar:${
                          isShowFilter ? 'hideFilters' : 'showFilters'
                        }`,
                      )}
                      arrow
                      placement="right"
                    >
                      <IconButton
                        size="small"
                        aria-label="show/hide filters"
                        sx={{
                          outline: 'none',
                        }}
                        onClick={() => setIsShowFilter(prev => !prev)}
                      >
                        {!isShowFilter ? (
                          <FilterListIcon
                            sx={{
                              fontSize: 'var(--font-size-xl)',
                            }}
                          />
                        ) : (
                          <FilterListOffIcon
                            sx={{
                              fontSize: 'var(--font-size-xl)',
                            }}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                  ) : (
                    ''
                  );
                },
                Cell: ({ row: { index, original } }) => {
                  // для редактирования строки должен быть параметр uniqId
                  const { isEdit, uniqId } = original;
                  const hasWordIdField = Object.prototype.hasOwnProperty.call(
                    original,
                    'word_id',
                  );
                  const canShowRowActions =
                    !hasWordIdField ||
                    (original.word_id !== null &&
                      original.word_id !== undefined);
                  return (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        flexWrap: 'nowrap',
                      }}
                    >
                      {actionColumnComponent?.(original)}
                      {editRow && canShowRowActions && (
                        <>
                          {!isEdit ? (
                            <Tooltip
                              title={t('promptbar:edit')}
                              arrow
                              placement="right"
                            >
                              <IconButton
                                size="sm"
                                aria-label="edit cell"
                                onClick={() => onEdit(uniqId)}
                              >
                                <EditTwoToneIcon
                                  sx={{
                                    fontSize: 'var(--font-size-xl)',
                                    color: 'var(--color-yellow-dark)',
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Tooltip
                                arrow
                                placement="right"
                                title={t('promptbar:save')}
                              >
                                <IconButton
                                  size="sm"
                                  color="success"
                                  variant="outlined"
                                  aria-label="save row"
                                  className="save-btn"
                                  sx={{
                                    backgroundColor: 'var(--color-green-100)',
                                    '&:hover': {
                                      backgroundColor: 'var(--color-green-100)',
                                    },
                                  }}
                                  onClick={() => applyChanges(uniqId, original)}
                                >
                                  <DoneIcon
                                    sx={{
                                      fontSize: 'var(--font-size-m)',
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>

                              <Tooltip
                                arrow
                                placement="right"
                                title={t('promptbar:cancel')}
                              >
                                <IconButton
                                  size="sm"
                                  color="error"
                                  variant="outlined"
                                  aria-label="cancel editing"
                                  className="cancel-btn"
                                  sx={{
                                    marginTop: '3px',
                                    backgroundColor: 'var(--color-red-100)',
                                    '&:hover': {
                                      backgroundColor: 'var(--color-red-100)',
                                    },
                                  }}
                                  onClick={() => cancelChanges(uniqId)}
                                >
                                  <CloseIcon
                                    sx={{
                                      fontSize: 'var(--font-size-m)',
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  );
                },
              },
              ...columns,
            ]
          : [...columns],
      );
    },
  );

  useEffect(() => {
    setIsShowFilter(isFilter);
  }, [isFilter]);

  useEffect(() => {
    resetPage();
    setGlobalFilter(customGlobalFilter);
  }, [customGlobalFilter, setGlobalFilter]);

  useEffect(() => {
    setHiddenColumns(
      columns.filter(col => col.show === false).map(col => col.id),
    );
  }, [columns, setHiddenColumns]);

  useEffect(() => {
    if (!fetchData) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const params = {
      offset: fetchId * pageSize,
      limit: pageSize,
      globalFilter,
    };
    filters.forEach(filter => (params[filter.id] = filter.value));

    fetchData({ params, signal });

    return () => controller.abort();
  }, [fetchData, fetchId, filters, pageSize, globalFilter]);

  const tableComponent = (
    <TableContainer>
      <Table
        aria-label="table"
        {...getTableProps()}
        className={'filtered' + type ? ` ${type}` : ''}
      >
        <TableHead>
          {headerGroups.map(headerGroup => {
            const { key, ...props } = headerGroup.getHeaderGroupProps();
            return (
              <TableRow key={key} {...props}>
                {headerGroup.headers.map(column => {
                  const { key, ...props } = column.getHeaderProps({
                    style: column.style,
                    className: column.className,
                  });
                  return (
                    <TableCell key={key} {...props}>
                      {column.render('Header')}
                      {column.canFilter ? column.render('Filter') : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            const { key, ...props } = row.getRowProps({
              style: { height: '50px' },
            });
            return (
              <TableRow
                key={key}
                {...props}
                sx={getRowSx?.(row.original, row.index)}
              >
                {row.cells.map(cell => {
                  const { key, ...props } = cell.getCellProps({
                    className: cell.column.className,
                    style: cell.column.style,
                  });
                  return (
                    <TableCell
                      key={key}
                      {...props}
                      sx={{
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      {cell.render('Cell')}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return isInfiniteScroll ? (
    <div ref={scrollableWrapperRef}>
      <InfiniteScroll
        ref={infiniteScrollRef}
        dataLength={data.length}
        next={() => setFetchId(prev => prev + 1)}
        hasMore={hasMoreData}
        loader={null}
        scrollThreshold={0.9}
        height={
          window.innerHeight -
          (scrollableWrapperRef.current?.offsetTop || 0) -
          10
        }
      >
        {tableComponent}
      </InfiniteScroll>
    </div>
  ) : (
    tableComponent
  );
};

FunctionalTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  type: PropTypes.string,
  onEdit: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchData: PropTypes.func,
  applyChanges: PropTypes.func,
  cancelChanges: PropTypes.func,
  isActionColumn: PropTypes.bool,
  hasMoreData: PropTypes.bool,
  isInfiniteScroll: PropTypes.bool,
  pageSize: PropTypes.number,
  isFilter: PropTypes.bool,
  editRow: PropTypes.bool,
  globalFilter: PropTypes.string,
  actionColumnComponent: PropTypes.func,
  getRowSx: PropTypes.func,
};

export default FunctionalTable;
